import React from 'react';
import Icon from '../../../components/AppIcon';

const KeyFindingsCard = ({ findings }) => {
  const getSeverityBadge = (severity) => {
    const badges = {
      critical: { bg: 'bg-error/10', text: 'text-error', icon: 'AlertTriangle' },
      high: { bg: 'bg-warning/10', text: 'text-warning', icon: 'AlertCircle' },
      medium: { bg: 'bg-primary/10', text: 'text-primary', icon: 'Info' },
      low: { bg: 'bg-muted', text: 'text-muted-foreground', icon: 'CheckCircle' }
    };
    return badges?.[severity] || badges?.medium;
  };

  const getTypeBadge = (type) => {
    const badges = {
      violation: { bg: 'bg-error/10', text: 'text-error', label: 'Violation' },
      improvement: { bg: 'bg-success/10', text: 'text-success', label: 'Improvement' },
      achievement: { bg: 'bg-primary/10', text: 'text-primary', label: 'Achievement' },
      risk: { bg: 'bg-warning/10', text: 'text-warning', label: 'Risk' }
    };
    return badges?.[type] || badges?.violation;
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString)?.toLocaleDateString('en-PG', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Search" size={20} color="var(--color-primary)" />
          <h3 className="font-heading font-semibold text-base md:text-lg text-foreground">
            Key Findings
          </h3>
        </div>
        <p className="caption text-xs md:text-sm text-muted-foreground mt-1">
          Critical insights and recommendations from compliance analysis
        </p>
      </div>

      <div className="p-4 md:p-6">
        <div className="space-y-4">
          {findings?.map((finding, index) => {
            const severityBadge = getSeverityBadge(finding?.severity);
            const typeBadge = getTypeBadge(finding?.findingType);

            return (
              <div key={finding?.id || index} className="border border-border rounded-lg p-4 hover:shadow-sm transition-smooth">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Icon 
                      name={severityBadge?.icon} 
                      size={18} 
                      color={severityBadge?.text?.replace('text-', 'var(--color-)')} 
                    />
                    <h4 className="font-heading font-semibold text-sm text-foreground">
                      {finding?.title}
                    </h4>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-1 ${typeBadge?.bg} ${typeBadge?.text} text-xs font-caption rounded-full`}>
                      {typeBadge?.label}
                    </span>
                    <span className={`px-2 py-1 ${severityBadge?.bg} ${severityBadge?.text} text-xs font-caption rounded-full capitalize`}>
                      {finding?.severity}
                    </span>
                  </div>
                </div>

                <p className="caption text-sm text-muted-foreground mb-3">
                  {finding?.description}
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  {finding?.affectedDepartments && finding?.affectedDepartments?.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Icon name="Building2" size={14} color="var(--color-muted-foreground)" />
                      <p className="caption text-xs text-muted-foreground">
                        Departments: <span className="font-medium text-foreground">{finding?.affectedDepartments?.join(', ')}</span>
                      </p>
                    </div>
                  )}
                  {finding?.affectedEmployeeCount > 0 && (
                    <div className="flex items-center gap-2">
                      <Icon name="Users" size={14} color="var(--color-muted-foreground)" />
                      <p className="caption text-xs text-muted-foreground">
                        Affected: <span className="font-medium text-foreground">{finding?.affectedEmployeeCount} employees</span>
                      </p>
                    </div>
                  )}
                </div>

                {finding?.recommendation && (
                  <div className="p-3 bg-primary/5 border-l-4 border-primary rounded mb-3">
                    <p className="caption text-xs font-medium text-foreground mb-1">Recommendation</p>
                    <p className="caption text-xs text-muted-foreground">{finding?.recommendation}</p>
                  </div>
                )}

                {finding?.actionRequired && (
                  <div className="flex items-center justify-between p-2 bg-warning/10 rounded">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertTriangle" size={14} color="var(--color-warning)" />
                      <p className="caption text-xs font-medium text-warning">Action Required</p>
                    </div>
                    {finding?.deadline && (
                      <div className="flex items-center gap-1">
                        <Icon name="Calendar" size={12} color="var(--color-warning)" />
                        <p className="caption text-xs text-warning">Due: {formatDate(finding?.deadline)}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {(!findings || findings?.length === 0) && (
          <div className="py-8 text-center">
            <Icon name="Search" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
            <p className="caption text-sm text-muted-foreground">No key findings available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KeyFindingsCard;